import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { BarChart3, Plus, Edit, Trash2 } from 'lucide-react';

export const KPIMetricsTab = ({
  filteredKpis, statusFilter, statusFilterLabel, categories,
  isGuest, isAdmin, isMasterAdmin,
  setEditingKPI, handleDeleteKPI, setDialogOpen,
  getTrendIcon, getPerformanceColor, getPerformanceBg
}) => (
  <div className="space-y-6">
    {filteredKpis.length > 0 ? (
      <>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold" data-testid="metrics-list-heading">{statusFilterLabel}</h2>
          <p className="text-sm text-muted-foreground">{filteredKpis.length} KPI{filteredKpis.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredKpis.map((kpi) => {
            const performance = kpi.target > 0 ? (kpi.value / kpi.target) * 100 : 0;
            return (
              <Card key={kpi.metric_id} className={`card-hover border ${getPerformanceBg(performance)}`} data-testid={`kpi-card-${kpi.metric_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize" style={{ borderColor: categories.find(c => c.value === kpi.category)?.color }}>
                        {kpi.category}
                      </Badge>
                      {getTrendIcon(kpi.trend)}
                      {isGuest && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-sky-300 text-sky-600 bg-sky-50">Sample</Badge>
                      )}
                    </div>
                    {!isGuest && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8"
                          onClick={() => setEditingKPI({...kpi, category: (kpi.category || '').toLowerCase()})}
                          data-testid={`edit-kpi-${kpi.metric_id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {isMasterAdmin && (
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700"
                            onClick={() => handleDeleteKPI(kpi.metric_id)}
                            data-testid={`delete-kpi-${kpi.metric_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  <CardTitle className="text-base mt-2">{kpi.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end justify-between mb-3">
                    <div>
                      <span className={`text-3xl font-bold ${getPerformanceColor(kpi.value, kpi.target)}`}>{kpi.value}</span>
                      <span className="text-sm text-muted-foreground ml-1">{kpi.unit}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Target</p>
                      <p className="font-medium">{kpi.target}{kpi.unit}</p>
                    </div>
                  </div>
                  <Progress value={Math.min(performance, 100)} className="h-2" />
                  <p className="text-xs text-muted-foreground mt-2">{Math.round(performance)}% of target</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </>
    ) : (
      <Card className="py-16">
        <CardContent className="text-center">
          <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h3 className="text-lg font-semibold mb-2">
            {statusFilter === 'all' ? 'No KPIs found' : `No ${statusFilter === 'on_target' ? 'on target' : 'needs attention'} KPIs`}
          </h3>
          <p className="text-muted-foreground font-body mb-4">
            {statusFilter === 'all' ? 'Start tracking your key metrics' : 'Try selecting a different filter'}
          </p>
          {statusFilter === 'all' && (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Add KPI
            </Button>
          )}
        </CardContent>
      </Card>
    )}
  </div>
);
